export type Spring = { stiffness: number; damping: number };

export type Channel = {
    value: number;
    velocity: number;
    target: number;
    stiffness: number;
    damping: number;
    restThreshold: number;
};

export type Ball = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    angle: number;
    spin: number;
    size: number;
    bounces: number;
    column: number;
};

export type Court = {
    width: number;
    height: number;
    baselineHeight: number;
    columnEnds: readonly number[];
    tops: readonly number[];
};

export type Landing = { column: number; speed: number };

export const TUNING = {
    entrance: { visibleRatio: 0.4, floatingChromeZone: "15%" },
    hover: {
        liftBaselineHeights: 0.2,
        tiltDeg: [2, 5],
        reachLetterWidths: 1.1,
        spring: { stiffness: 420, damping: 26 },
        settle: { stiffness: 260, damping: 22 },
    },
    fetch: {
        sizeBaselineHeights: 0.16,
        minSizePx: 22,
        maxSizePx: 72,
        gravityBaselineHeightsPerSec2: 9,
        launchBaselineHeightsPerSec: [2.5, 3.8],
        driftBaselineHeightsPerSec: 1.9,
        spinDegPerSec: 540,
        restitution: 0.62,
        maxBounces: 4,
        maxBalls: 20,
        landingLiftShare: 0.6,
        landingSquashShare: 0.65,
        climbableLedgeBallSizes: 0.15,
        recoilBaselineHeightsPerSec: 1.6,
        recoilStretchPerSec: 2.5,
        stretchWidthRatio: 0.6,
    },
    engine: {
        substepSec: 1 / 240,
        maxFrameSec: 0.05,
        restPx: 0.1,
        restDeg: 0.02,
        restScale: 0.002,
    },
} as const;

export function createChannel(
    value: number,
    restThreshold: number,
    spring: Spring,
): Channel {
    return { value, velocity: 0, target: value, restThreshold, ...spring };
}

export function stepChannel(channel: Channel, dt: number): void {
    const force =
        -channel.stiffness * (channel.value - channel.target) -
        channel.damping * channel.velocity;
    channel.velocity += force * dt;
    channel.value += channel.velocity * dt;
}

export function settleChannel(channel: Channel): boolean {
    const moving =
        Math.abs(channel.value - channel.target) > channel.restThreshold ||
        Math.abs(channel.velocity) > channel.restThreshold;
    if (!moving) {
        channel.value = channel.target;
        channel.velocity = 0;
    }
    return moving;
}

export function waveAmount(
    pointerX: number,
    letterCenter: number,
    letterWidth: number,
): number {
    const distance =
        (pointerX - letterCenter) /
        (letterWidth * TUNING.hover.reachLetterWidths);
    return Math.exp(-distance * distance);
}

export function rollTilt(random: () => number): number {
    const [min, max] = TUNING.hover.tiltDeg;
    const direction = random() < 0.5 ? -1 : 1;
    return direction * (min + random() * (max - min));
}

export function ballSize(baselineHeight: number): number {
    const { sizeBaselineHeights, minSizePx, maxSizePx } = TUNING.fetch;
    return Math.min(
        maxSizePx,
        Math.max(minSizePx, baselineHeight * sizeBaselineHeights),
    );
}

export function columnAt(x: number, columnEnds: readonly number[]): number {
    const column = columnEnds.findIndex((end) => x <= end);
    return column === -1 ? columnEnds.length - 1 : column;
}

export function throwBall(
    column: number,
    centerX: number,
    court: Court,
    random: () => number,
): Ball {
    const {
        launchBaselineHeightsPerSec: launch,
        driftBaselineHeightsPerSec: drift,
        spinDegPerSec: spin,
    } = TUNING.fetch;
    const between = (min: number, max: number) => min + random() * (max - min);
    const size = ballSize(court.baselineHeight);
    return {
        x: centerX,
        y: (court.tops[column] ?? 0) - size / 2,
        vx: between(-drift, drift) * court.baselineHeight,
        vy: -between(launch[0], launch[1]) * court.baselineHeight,
        angle: between(0, 360),
        spin: between(-spin, spin),
        size,
        bounces: 0,
        column,
    };
}

export function stepBall(
    ball: Ball,
    dt: number,
    court: Court,
    random: () => number,
): Landing | null {
    const {
        gravityBaselineHeightsPerSec2: gravity,
        restitution,
        climbableLedgeBallSizes,
        spinDegPerSec: spin,
    } = TUNING.fetch;
    const half = ball.size / 2;
    const fromX = ball.x;

    ball.vy += gravity * court.baselineHeight * dt;
    ball.x += ball.vx * dt;
    ball.y += ball.vy * dt;
    ball.angle += ball.spin * dt;

    const pastLeft = ball.x < half && ball.vx < 0;
    const pastRight = ball.x > court.width - half && ball.vx > 0;
    if (pastLeft || pastRight) {
        ball.x = Math.max(half, Math.min(court.width - half, ball.x));
        ball.vx = -ball.vx * restitution;
        ball.spin = -ball.spin;
    }

    if (isSpent(ball)) return null;

    let column = columnAt(ball.x, court.columnEnds);
    const ledge =
        (court.tops[column] ?? 0) + ball.size * climbableLedgeBallSizes;
    if (column !== ball.column && ball.y + half > ledge) {
        ball.x = fromX;
        ball.vx = -ball.vx * restitution;
        column = ball.column;
    }
    ball.column = column;

    const top = court.tops[column] ?? 0;
    if (ball.y + half < top) return null;
    ball.y = top - half;
    if (ball.vy <= 0) return null;

    const speed = ball.vy;
    ball.vy = -ball.vy * restitution;
    ball.spin = (random() * 2 - 1) * spin;
    ball.bounces += 1;
    return { column, speed };
}

export function isBallGone(ball: Ball, court: Court): boolean {
    return ball.y - ball.size / 2 > court.height + ball.size;
}

function isSpent(ball: Ball): boolean {
    return ball.bounces >= TUNING.fetch.maxBounces;
}

export function retireOldestPastLimit(balls: readonly Ball[]): void {
    const bouncing = balls.filter((ball) => !isSpent(ball));
    const oldest = bouncing[0];
    if (bouncing.length > TUNING.fetch.maxBalls && oldest) {
        oldest.bounces = TUNING.fetch.maxBounces;
    }
}
